import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import cx from 'classnames';
import { useQueryClient } from 'react-query';

import Button from '~/components/button';
import { PestPlanTypes } from '~/components/pest/bundles/pest-plan-content';
import { seasonalCopy } from '~/components/plans/bundles-page/plan-bundle-page';
import { lawnSubscriptionStatus } from '~/features/lawns/lawn';
import { useAddBundleToCart } from '~/components/plans/bundles-page/use-add-bundle-to-cart';
import { useLawn } from '~/features/lawns/lawn-actions';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { useCart } from '~/hooks/use-cart';
import { useSession } from '~/features/session/session-actions';
import { captureException } from '~/utils/exception-tracking';
import { centsToCurrency } from '~/utils/numbers';
import { toSentenceCase } from '~/utils/strings';
import { pestPlanNextBestRoute } from '~/routes';
import {
  ARE_TIERED_PLAN_DISCOUNTS_ENABLED_FLAG,
  IS_FIRST_APPLICATION_TRIAL_ENABLED_FLAG,
  IS_SEASON_TRUNCATED_FLAG,
} from '~/feature-flags';
import Tooltip from '~/components/tooltip/tooltip';
import { toLongFullDateString } from '~/utils/dates';
import { addTieredDiscountCoupon } from '~/features/cart/cart';
import { navigateToNextFunnelPage } from '~/components/plans/helpers';
import { YARD_CONTINUATION_TEST } from '~/utils/vwo-tests';
import { useVwoExperiment } from '~/hooks/use-vwo-experiment';

import styles from '~/components/plans/bundles-page/plan-bundle-page.module.scss';

const BUG_ICONS = {
  [PestPlanTypes.MOSQUITO]: {
    src: '/icons/pest/mosquito-deleto-desk.svg',
    height: '30rem',
    top: '-28rem',
    alt: 'Illustration of a mosquito dying after being sprayed with Sunday Mosquito Deleto pest protection',
  },
  [PestPlanTypes.TOTAL_HOME]: {
    src: '/icons/pest/bug-doom-desk.svg',
    height: '30rem',
    top: '-30.1rem',
    alt: ' Illustration of a tick dying after being sprayed with Sunday preventive pest control',
  },
  [PestPlanTypes.TICK]: {
    src: '/icons/pest/tick-desk.svg',
    height: '30rem',
    top: '-30.1rem',
    alt: ' Illustration of a house fly dying after being sprayed with Sunday Bug Doom pest protection',
  },
};

export const FEATURE_ICONS = {
  soil: { src: '/icons/soil-icon-filled.svg', height: '28px' },
  weed: { src: '/icons/weed-icon-filled.svg', height: '24px' },
  seed: { src: '/icons/soil-grass-icon-filled.svg', height: '35px' },
  package: { src: '/icons/closed-package-icon-filled.svg', height: '35px' },
  pouch: { src: '/icons/pouch-icon-filled.svg', height: '28px' },
  calendar: { src: '/icons/calendar-icon-filled.svg', height: '35px' },
  ant: { src: '/icons/bundle-ant-icon.svg', height: '35px' },
  mosquito: { src: '/icons/bundle-mosquito-icon.svg', height: '35px' },
  bug: { src: '/icons/bundle-bug-icon.svg', height: '30px' },
  pet: { src: '/icons/pet-icon.svg', height: '30px' },
  gift: { src: '/icons/icon-gift-green.svg', height: '30px' },
  tools: { src: '/icons/icon-tools-green.svg?v3', height: '30px' },
  seedBag: { src: '/icons/seed-bag.svg?v2', height: '28px' },
  flowerGreen: { src: '/icons/garden-icon.svg', height: '28px' },
  tomato: { src: '/icons/green-tomato.svg', height: '28px' },
  tree: { src: '/icons/green-tree.svg', height: '30px' },
  plus: { src: '/icons/plus-sign.svg', height: '28px' },

  // Blue pest icons below
  packageBlue: { src: '/icons/pest/blue-package.svg', height: '35px' },
  mosquitoBlue: { src: '/icons/pest/mosquito-blue.svg', height: '29px' },
  candle: { src: '/icons/pest/candle-blue.svg', height: '38px' },
  dunk: { src: '/icons/pest/dunk-blue.svg', height: '26px' },
  spray: { src: '/icons/pest/spray-blue.svg', height: '33px' },
  sprayBottle: { src: '/icons/pest/spray-bottle-blue.svg', height: '37px' },
  bugDoom: { src: '/icons/pest/bug-doom-blue.svg', height: '31px' },
  bugDoomRefill: {
    src: '/icons/pest/bug-doom-refill-blue.svg',
    height: '29px',
  },
  antAdios: { src: '/icons/pest/ant-adios-blue.svg', height: '35px' },
  nixTicks: { src: '/icons/pest/nix-ticks-blue.svg', height: '39px' },
  houseBlue: { src: '/icons/pest/home-blue.svg', height: '39px' },
  giftBlue: { src: '/icons/icon-gift-blue.svg', height: '30px' },
  toolsBlue: { src: '/icons/icon-tools-blue.svg?v3', height: '30px' },

  // DEFAULT = Green pouch icon
  default: { src: '/icons/pouch-icon-filled.svg', height: '28px' },
};

function PlanBundle({
  bundle,
  isFlexPay,
  lastItem,
  onSelect = () => {},
  planType,
  showFeatures = true,
  id,
  showHardmarkPricing,
  isRecommended,
  firstShipmentDate,
  ctaText = 'Add to cart',
}) {
  const { lawn } = useLawn();
  const isPestPlan = Object.keys(PestPlanTypes).includes(planType);

  const { title, tagline, banner, features } = bundle;

  const showSoilTestDiscount = lawn?.hasSoilTest;

  return (
    <div className={cx('panel', styles.planBundle)} id={id}>
      {lastItem && isPestPlan && (
        <img
          className={styles.bugIcon}
          alt={BUG_ICONS[planType]?.alt}
          src={BUG_ICONS[planType]?.src}
          style={{
            height: BUG_ICONS[planType]?.height,
            top: BUG_ICONS[planType]?.top,
          }}
        />
      )}
      {banner && (
        <div className={styles.planBundleBanner} id={`${id}-banner`}>
          <p>{banner}</p>
        </div>
      )}
      <div
        className={cx({
          [styles.colorBlock]: isPestPlan,
        })}
      >
        <p
          className={cx(styles.planBundleTitle, {
            [styles.extraBottomMargin]: !isPestPlan,
          })}
          id={`${id}-title`}
        >
          {title}
        </p>
        {isPestPlan && (
          <p className={styles.planBundleTagline} id={`${id}-tagline`}>
            {tagline}
          </p>
        )}
      </div>
      {showFeatures && (
        <ul className={styles.contentsList}>
          {features?.map((feature, i) => (
            <FeatureItem feature={feature} key={`${feature.text}-${i}`} />
          ))}
        </ul>
      )}

      <PlanBundleFooter
        bundle={bundle}
        currentPlanYear={lawn?.currentPlanYear}
        isFlexPay={isFlexPay}
        isPestPlan={isPestPlan}
        lawnUuid={lawn?.uuid}
        onSelect={onSelect}
        id={`${id}-footer`}
        showHardmarkPricing={showHardmarkPricing}
        showSoilTestDiscount={showSoilTestDiscount}
        isRecommended={isRecommended}
        firstShipmentDate={firstShipmentDate}
        ctaText={ctaText}
      />
    </div>
  );
}

export default PlanBundle;

export const FeatureItem = ({ className, feature }) => {
  return (
    <li className={cx(styles.contentsItem, className)}>
      <div className={styles.contentsItemIcon}>
        <img
          src={FEATURE_ICONS[feature?.icon]?.src || FEATURE_ICONS.default.src}
          style={{
            height:
              FEATURE_ICONS[feature?.icon]?.height ||
              FEATURE_ICONS.default.height,
            width: 'auto',
          }}
          alt={feature?.icon}
        />
      </div>
      <p className={styles.contentsItemQuantity}>{feature?.quantity}</p>
      <p className={styles.contentsItemText}>{feature?.text}</p>
    </li>
  );
};

function PlanBundleFooter({
  bundle,
  lawnUuid,
  currentPlanYear = '2024',
  onSelect,
  isFlexPay,
  isPestPlan,
  id,
  showHardmarkPricing,
  showSoilTestDiscount,
  isRecommended,
  firstShipmentDate,
  ctaText,
}) {
  const navigate = useNavigate();
  const { user } = useSession();
  const { showFlashMessage } = useFlashMessage();
  const { addBundleToCart, isAddingToCart: isAddingLawnBundleToCart } =
    useAddBundleToCart({
      lawnUuid,
      isFlexPay,
    });
  const [isAddingPestPlanToCart, setIsAddingPestPlanToCart] = useState(false);
  const isAddingToCart = isAddingLawnBundleToCart || isAddingPestPlanToCart;
  const { cart } = useCart();
  const { addToCart } = useCartMutations();
  const queryClient = useQueryClient();
  const isFirstApplicationTrialEnabled =
    IS_FIRST_APPLICATION_TRIAL_ENABLED_FLAG;
  const { getVariantName: getYardContinuationVariantName } = useVwoExperiment(
    YARD_CONTINUATION_TEST.NAME,
    { activateOnLoad: false }
  );
  const skipContinuationPage =
    getYardContinuationVariantName() ===
    YARD_CONTINUATION_TEST.SKIP_PAGES_VARIANT;

  const numOfShipments = bundle?.carts?.length || 1;

  let periodText = isPestPlan ? '/year' : isFlexPay ? 'today' : seasonalCopy;

  if (isFirstApplicationTrialEnabled) {
    periodText = periodText.replace('/', '');
  }

  const pestPlanExists =
    isPestPlan &&
    Boolean(
      cart?.contents?.find(
        (item) => item.sku?.skuId === bundle?.purchaseSku?.skuId
      )
    );

  const handleAddToCart = async () => {
    if (isPestPlan) {
      setIsAddingPestPlanToCart(true);

      if (pestPlanExists) {
        showFlashMessage(
          `${bundle?.title} - ${toSentenceCase(
            bundle?.size
          )} is in your cart, please select another size.`,
          'error'
        );
        return setIsAddingPestPlanToCart(false);
      }

      try {
        await addToCart(bundle?.purchaseSku, 1, {
          isSubscription: true,
        });

        showFlashMessage(
          `${bundle?.title} - ${toSentenceCase(
            bundle?.size
          )} added to your cart`,
          'success',
          5000
        );

        // The navigate timeout should ensure that the analytics event is sent before the
        // user is navigated to the add-ons page.

        setTimeout(() => {
          navigate(pestPlanNextBestRoute(lawnUuid));
        }, 0);
      } catch (err) {
        captureException(new Error('Error when adding a pest plan to cart'), {
          extras: {
            error: JSON.stringify(err),
            planSku: JSON.stringify(bundle?.purchaseSku),
          },
        });

        showFlashMessage(
          'Oops, there was a problem adding the plan to your cart',
          'error'
        );
      }
      setIsAddingPestPlanToCart(false);

      return;
    }

    await addBundleToCart(bundle, isFlexPay, isRecommended);

    const isTieredPlanDiscountsEnabled = ARE_TIERED_PLAN_DISCOUNTS_ENABLED_FLAG;
    if (isTieredPlanDiscountsEnabled) {
      await addTieredDiscountCoupon(
        bundle?.selectedCartUuid,
        addTieredDiscountCoupon,
        queryClient,
        showFlashMessage
      );
    }
    const isYardFunnelEnabled = true;

    navigateToNextFunnelPage(
      isYardFunnelEnabled,
      skipContinuationPage,
      lawnUuid,
      navigate
    );
    return;
  };

  const firstBoxSubtotalPrice = isPestPlan
    ? bundle?.unitPrice
    : isFlexPay
      ? bundle?.carts?.[0].subtotal
      : bundle?.fullSeasonPrice;

  const firstBoxActualPrice = isPestPlan
    ? bundle?.unitPrice
    : isFlexPay
      ? bundle?.carts?.[0].discountedPrice
      : bundle?.fullSeasonDiscountedPrice;

  const firstBoxMsrpPrice = isPestPlan
    ? bundle?.unitPrice
    : isFlexPay
      ? bundle?.carts?.[0].msrp
      : bundle?.fullSeasonMsrp;

  let tooltipCopy = `Pay as you go with several shipments this year containing
      only what your lawn needs, at exactly the right time.`;

  if (isFirstApplicationTrialEnabled) {
    tooltipCopy = `You can expect several shipments this year containing
      only what your lawn needs, at exactly the right time.
      For more info see Plan Details.`;
  }

  const showSoilTestDiscountPill =
    user?.lawnSubscriptionStatus !== lawnSubscriptionStatus.CANCELLED &&
    !isPestPlan &&
    showSoilTestDiscount &&
    firstBoxSubtotalPrice > firstBoxActualPrice;

  const showHardmarkPricingPill =
    showHardmarkPricing && firstBoxMsrpPrice > firstBoxActualPrice;

  return (
    <div className={styles.planBundleFooter} id={id}>
      {showSoilTestDiscountPill ? (
        <PlanSavings
          msrp={firstBoxSubtotalPrice}
          price={firstBoxActualPrice}
          title="20% off with SOILTEST coupon"
        />
      ) : showHardmarkPricingPill ? (
        <PlanSavings msrp={firstBoxMsrpPrice} price={firstBoxActualPrice} />
      ) : null}
      <div className={styles.planBundlePriceContainer}>
        <div className={styles.planBundlePrice}>
          <span>{centsToCurrency(firstBoxActualPrice)}</span>
          {isPestPlan ? (
            <span className={styles.planBundlePeriod}>&nbsp;{periodText}</span>
          ) : (
            numOfShipments > 1 && (
              <>
                <span className={styles.planBundlePeriod}>
                  &nbsp;{periodText}
                </span>
                <Tooltip
                  className={styles.planCardTooltip}
                  content={
                    <div className={styles.planCardTooltipContent}>
                      {tooltipCopy}
                    </div>
                  }
                />
              </>
            )
          )}
        </div>
      </div>
      {isFlexPay &&
        (IS_SEASON_TRUNCATED_FLAG ? (
          numOfShipments > 1 && (
            <p className={styles.planDetailAboveButton}>
              {centsToCurrency(bundle?.fullSeasonPrice)} total for this year
            </p>
          )
        ) : (
          <p className={styles.planDetailAboveButton}>
            {centsToCurrency(bundle?.fullSeasonPrice)}
            {seasonalCopy}
          </p>
        ))}
      <Button isLoading={isAddingToCart} fullWidth onClick={handleAddToCart}>
        {ctaText}
      </Button>
      {firstShipmentDate && (
        <p className={cx(styles.expectedShipDate, styles.card)}>
          Ships on {toLongFullDateString(firstShipmentDate)}.
        </p>
      )}
    </div>
  );
}

export const PlanSavings = ({
  retailPrice,
  actualPrice,
  title = '',
  className,
}) => {
  return (
    <span className={cx(styles.planBundleSavingsContainer, className)}>
      <PlanSavingsPill
        retailPrice={retailPrice}
        actualPrice={actualPrice}
        title={title}
      />
      <span className={styles.planBundleMsrpPrice}>
        {centsToCurrency(retailPrice)}
      </span>
    </span>
  );
};

export const PlanSavingsPill = ({
  retailPrice,
  actualPrice,
  title = '',
  discountPercent = null,
  className,
}) => {
  const savings = discountPercent
    ? discountPercent
    : Math.round(((retailPrice - actualPrice) / retailPrice) * 100);

  return (
    <span className={cx(styles.planBundleSavings, className)} title={title}>
      SAVE {savings}%
    </span>
  );
};
